import type { ReactNode } from "react";
import { Card, Tag, Typography, Button, Space, Row, Col, Grid } from "antd";
import { EyeOutlined, EditOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { GridColumnMetadata, LifecycleInfo } from "../../types";
import { snakeToCamel } from "../../utils/fieldMapping";
import { getItemTitle } from "../../utils/itemTitle";

const { Text } = Typography;
const { useBreakpoint } = Grid;

type RenderFn = (value: unknown, record: Record<string, unknown>) => ReactNode;

const CARD_DISPLAY_FIELD_COUNT = 6;

const COLUMN_COLORS = [
  "#1890ff",
  "#13c2c2",
  "#722ed1",
  "#faad14",
  "#52c41a",
  "#eb2f96",
  "#fa541c",
  "#2f54eb",
];

function getStateColor(
  state: string | undefined,
  lifecycleMap: LifecycleInfo | null,
): string {
  if (!state || !lifecycleMap?.states) return "blue";
  const sorted = [...lifecycleMap.states].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );
  const idx = sorted.findIndex((s) => s.name === state);
  if (idx === -1) return "blue";
  return COLUMN_COLORS[idx % COLUMN_COLORS.length];
}

const TITLE_FIELDS = ["name", "item_number", "keyed_name", "display_name", "full_name", "class_name"];

interface ItemCardProps {
  record: Record<string, unknown>;
  columns: GridColumnMetadata[];
  resource: string;
  enableRowClick?: boolean;
  includeActions?: boolean;
  mergedRenderers: Record<string, RenderFn>;
  getRenderer: (col: GridColumnMetadata) => RenderFn;
  lifecycleMap: LifecycleInfo | null;
}

export function ItemCard({
  record,
  columns,
  resource,
  enableRowClick,
  includeActions = true,
  mergedRenderers,
  getRenderer,
  lifecycleMap,
}: ItemCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const referrerUrl = `${location.pathname}${location.search}`;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const id = record.id as string;

  const title = getItemTitle(record) ?? `#${id?.slice(0, 8) ?? ""}`;

  // State
  const state = record.state as string | undefined;
  const stateLabel = (record.stateLabel || state) as string | undefined;

  // Display columns: exclude title fields and state, then take first N
  const displayColumns = columns
    .filter((col) => {
      const camelName = col.name;
      if (TITLE_FIELDS.map(snakeToCamel).includes(camelName)) return false;
      if (camelName === "state" || camelName === "stateLabel") return false;
      return true;
    })
    .slice(0, CARD_DISPLAY_FIELD_COUNT);

  const actions = includeActions ? (
    <Space size={4} onClick={(e) => e.stopPropagation()}>
      <Button
        type="text"
        size="small"
        icon={<EyeOutlined />}
        onClick={() =>
          navigate(`/${resource}/${id}`, { state: { from: referrerUrl } })
        }
        aria-label={t("buttons.show", "View")}
      />
      <Button
        type="text"
        size="small"
        icon={<EditOutlined />}
        onClick={() =>
          navigate(`/${resource}/${id}/edit`, {
            state: { from: referrerUrl },
          })
        }
        aria-label={t("buttons.edit", "Edit")}
      />
    </Space>
  ) : undefined;

  return (
    <Card
      size="small"
      hoverable={enableRowClick}
      onClick={() => {
        if (enableRowClick && id) {
          navigate(`/${resource}/${id}/edit`, {
            state: { from: referrerUrl },
          });
        }
      }}
      style={{
        cursor: enableRowClick ? "pointer" : "default",
        height: "100%",
      }}
      styles={{ body: { padding: "12px 16px" } }}
      extra={actions}
    >
      {/* Title */}
      <Text
        strong
        ellipsis={{ tooltip: true }}
        style={{ display: "block", fontSize: 14, marginBottom: 4 }}
      >
        {title}
      </Text>

      {/* State tag */}
      {stateLabel && (
        <div style={{ marginBottom: 8 }}>
          <Tag color={getStateColor(state, lifecycleMap)}>{stateLabel}</Tag>
        </div>
      )}

      {/* Fields in 2-column grid */}
      <Row gutter={[16, 4]}>
        {displayColumns.map((col) => {
          const value = record[col.name];
          const renderer = mergedRenderers[col.name] || getRenderer(col);
          const renderedValue = renderer(value, record);

          return (
            <Col key={col.name} span={isMobile ? 24 : 12}>
              <div style={{ marginBottom: 2 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {t("table." + col.name, col.label)}
                </Text>
                <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                  {renderedValue}
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </Card>
  );
}
